import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, ArrowRight } from "lucide-react";
import api from "../lib/api";

const CATEGORIES = ["All", "Cleaning Tips", "Home Maintenance", "Commercial Care", "Lagos Living"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (q.trim()) params.q = q.trim();
    api.get("/posts", { params })
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div className="pt-24 lg:pt-32 pb-20 bg-brand-canvas min-h-screen" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">The Neat &amp; Proper Blog</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-emerald tracking-tight">Clean reads for clean spaces</h1>
          <p className="mt-4 text-brand-muted text-base sm:text-lg">Practical cleaning tips, home maintenance guides and Lagos living wisdom from our professional crews.</p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-brand-muted absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="blog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-[#D6E4DC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${category === c ? "bg-brand-emerald text-white" : "bg-white text-brand-muted border border-[#D6E4DC] hover:border-brand-lime"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="blog-loading"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-brand-muted" data-testid="blog-empty">No articles found. Try another search or category.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white rounded-3xl overflow-hidden border border-[#D6E4DC] hover:shadow-2xl hover:shadow-brand-emerald/10 hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="h-52 overflow-hidden relative">
                  <img src={p.cover_image || "/assets/hero.jpg"} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-4 left-4 bg-brand-emerald text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">{p.category}</span>
                </div>
                <div className="p-6">
                  <h2 className="font-display font-bold text-lg text-brand-emerald leading-snug group-hover:text-brand-lime transition-colors line-clamp-2">{p.title}</h2>
                  <p className="mt-2 text-sm text-brand-muted line-clamp-3 leading-relaxed">{p.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-brand-muted flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.reading_time} min read · {new Date(p.created_at).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <ArrowRight className="w-4 h-4 text-brand-gold group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
